import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  createContext,
  useContext
} from 'react'
import { createPortal } from 'react-dom'

const getActiveElement = () => new Promise((res) => setTimeout(() => res(document.activeElement)), 0)

/** CSS の max-height と合わせたドロップダウン高さの推定値（初回描画前の判定用） */
const DROPDOWN_MAX_HEIGHT = 300

/** トリガーとドロップダウンの間隔（px）。CSS の ui-select__list--above の 4px と揃える */
const DROPDOWN_GAP = 4

/**
 * 下方向の表示領域が足りない場合に上方向へ展開するか判定する
 *
 * @param {DOMRect} anchorRect
 * @param {number} dropdownHeight
 * @returns {boolean}
 */
const shouldOpenDropdownAbove = (anchorRect, dropdownHeight) => {
  const height = dropdownHeight > 0 ? dropdownHeight : DROPDOWN_MAX_HEIGHT
  const spaceBelow = window.innerHeight - anchorRect.bottom
  const spaceAbove = anchorRect.top
  return spaceBelow < height && spaceAbove > spaceBelow
}

/**
 * Overflow 祖先と window を順にスクロールし、要素の最下端が表示領域内に収まるようにする
 *
 * @param {HTMLElement} element
 */
const scrollElementBottomIntoView = (element) => {
  const scrollableAncestors = []
  let parent = element.parentElement
  const isScrollableOverflow = (v) => v === 'auto' || v === 'scroll' || v === 'overlay'
  while (parent) {
    const style = window.getComputedStyle(parent)
    // overflowY を優先。未展開（空文字）の環境（jsdom 等で shorthand の overflow のみ指定時）は overflow で補完する
    const overflowY = style.overflowY || style.overflow
    if (isScrollableOverflow(overflowY) && parent.scrollHeight > parent.clientHeight) {
      scrollableAncestors.push(parent)
    }
    parent = parent.parentElement
  }

  for (const ancestor of scrollableAncestors) {
    const bottomLimit = ancestor.getBoundingClientRect().bottom
    const overflow = element.getBoundingClientRect().bottom - bottomLimit
    if (overflow > 0) {
      ancestor.scrollTop += overflow
    }
  }

  const windowOverflow = element.getBoundingClientRect().bottom - window.innerHeight
  if (windowOverflow > 0) {
    window.scrollBy({ top: windowOverflow, behavior: 'smooth' })
  }
}

/**
 * ChrofyCustomSelect の振る舞いをツリー単位で切り替えるための Context。
 *
 * - `dropdownWidth: 'anchor'` (既定): ドロップダウンパネルの幅をトリガ要素の幅に固定する。
 * - `dropdownWidth: 'content'`: パネルはコンテンツ幅で開き、`min-width` をトリガ幅、 `max-width` を `dropdownMaxWidth` (既定 300px)
 *   で頭打ちにする。マッピング設定モーダル等、 選択肢ラベルが長く親レイアウト幅が狭い領域で使用する。
 */
export const ChrofyCustomSelectContext = createContext({
  dropdownWidth: 'anchor',
  dropdownMaxWidth: 300
})

/**
 * @typedef {Object} ChrofyCustomSelectProps
 * @property {string} id
 * @property {string} value
 * @property {string} name
 * @property {function(Event):void} onChange
 * @property {string} className
 * @property {SelectOption[]} options
 * @property {boolean} disabled
 * @property {boolean} isError
 * @property {boolean} hasNoSelect
 * @property {string} selectClassName
 * @property {string} dropdownClassName
 * @property {boolean} lightNoSelect
 * @property {'portal' | 'inline'} [dropdownPlacement] Portal: body へ fixed 描画（既定） / inline: トリガー直下に absolute
 *   描画（スクロール連動）
 * @property {boolean} [searchable] True の場合、展開時にコントロール内へ検索入力欄を表示し、入力文字でラベル部分一致による絞り込みを行う（既定 false）
 *
 * @typedef {Object} SelectOption
 * @property {string} label
 * @property {string} value
 * @property {any} extra
 */

/**
 * @function ChrofyCustomSelect
 * @type {React.FC<ChrofyCustomSelectProps>} props
 */

export const ChrofyCustomSelect = (props) => {
  const {
    id,
    name,
    value,
    onChange,
    className,
    options: _options = [],
    disabled,
    isError = false,
    hasNoSelect = true,
    selectClassName = '',
    dropdownClassName = '',
    lightNoSelect = true,
    placeholder = '',
    selectionKey = 'value',
    dropdownPlacement = 'portal',
    searchable = false
  } = props

  const isInlineDropdown = dropdownPlacement === 'inline'

  const [isOpenSelect, setIsOpenSelect] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState({})
  const [openAbove, setOpenAbove] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const containerRef = useRef(null)
  const selectRef = useRef(null)
  const searchInputRef = useRef(null)

  const optionsRef = useRef([])
  const dropdownRef = useRef(null)
  const portalOpenedBelowRef = useRef(true)

  const { dropdownWidth, dropdownMaxWidth } = useContext(ChrofyCustomSelectContext)
  const isContentWidth = dropdownWidth === 'content'

  const options = hasNoSelect ? [{ label: '未選択', value: '' }, ..._options] : _options

  const textLight = lightNoSelect && value === '' ? ' text-gray-700/50 ' : ''
  const errorClasses = ['is-error'].join(' ')

  const selectedLabel = options.find((e) => e[selectionKey] === value)?.label ?? placeholder

  // 検索語でラベル部分一致絞り込み。hasNoSelect の「未選択」(value='') は常に残す
  const filteredOptions = useMemo(() => {
    if (!searchable) return options
    const keyword = searchKeyword.trim().toLowerCase()
    if (!keyword) return options
    return options.filter((opt) => {
      if (hasNoSelect && opt.value === '') return true
      return String(opt.label ?? '')
        .toLowerCase()
        .includes(keyword)
    })
  }, [searchable, searchKeyword, options, hasNoSelect])

  const computeDropdownStyle = useCallback(() => {
    const anchor = containerRef.current ?? selectRef.current
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    const openAboveInitial = shouldOpenDropdownAbove(rect, DROPDOWN_MAX_HEIGHT)
    portalOpenedBelowRef.current = !openAboveInitial

    if (openAboveInitial) {
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        anchorWidth: rect.width,
        top: rect.top - DROPDOWN_MAX_HEIGHT - DROPDOWN_GAP,
        bottom: 'auto',
        openedBelow: false
      })
      return
    }

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      anchorWidth: rect.width,
      top: rect.bottom + DROPDOWN_GAP,
      bottom: 'auto',
      openedBelow: true
    })
  }, [])

  const resolveInlineOpenAbove = useCallback(() => {
    const anchor = containerRef.current ?? selectRef.current
    if (!anchor) return false
    return shouldOpenDropdownAbove(anchor.getBoundingClientRect(), DROPDOWN_MAX_HEIGHT)
  }, [])

  const openDropdown = () => {
    if (isInlineDropdown) {
      setOpenAbove(resolveInlineOpenAbove())
    } else {
      computeDropdownStyle()
    }
    // 展開時は検索語を空にし全候補を表示（選択済みラベルは placeholder で示す）
    if (searchable) setSearchKeyword('')
  }

  const closeDropdown = () => {
    setOpenAbove(false)
    portalOpenedBelowRef.current = true
    if (searchable) setSearchKeyword('')
  }

  const toggleSelect = (forceState) => {
    if (disabled) return

    if (forceState !== undefined) {
      if (forceState) {
        openDropdown()
      } else {
        closeDropdown()
      }
      setIsOpenSelect(forceState)
      return
    }

    if (!isOpenSelect) {
      openDropdown()
    } else {
      closeDropdown()
    }
    setIsOpenSelect((s) => !s)
  }

  /**
   * @function handleSelect 選択処理
   * @param {MouseEvent} e
   * @param {string} value
   */
  const handleSelect = (e, value) => {
    e?.stopPropagation()
    onChange?.({ target: { value } })
    closeDropdown()
    setIsOpenSelect(false)
  }
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = containerRef.current?.contains(event.target)
      const inDropdown = dropdownRef.current?.contains(event.target)
      if (!inTrigger && !inDropdown) {
        setOpenAbove(false)
        portalOpenedBelowRef.current = true
        setIsOpenSelect(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  // inline: 展開後にドロップダウン最下端がスクロール領域内に収まるよう自動スクロール
  useEffect(() => {
    if (!isOpenSelect || !isInlineDropdown) return

    const frameId = requestAnimationFrame(() => {
      if (!dropdownRef.current) return
      scrollElementBottomIntoView(dropdownRef.current)
    })

    return () => cancelAnimationFrame(frameId)
  }, [isOpenSelect, isInlineDropdown, openAbove])

  // portal: 初回は下方向で開き、はみ出す場合のみ上方向へ切り替え（下方向へ戻さない）
  useEffect(() => {
    if (!isOpenSelect || isInlineDropdown || !dropdownRef.current) return

    const anchor = containerRef.current ?? selectRef.current
    if (!anchor) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio < 1 && portalOpenedBelowRef.current) {
          const rect = anchor.getBoundingClientRect()
          const dropdownHeight = dropdownRef.current.getBoundingClientRect().height || dropdownRef.current.offsetHeight
          portalOpenedBelowRef.current = false
          setDropdownStyle({
            position: 'fixed',
            left: rect.left,
            anchorWidth: rect.width,
            top: rect.top - dropdownHeight - DROPDOWN_GAP,
            bottom: 'auto',
            openedBelow: false
          })
        }
      },
      { threshold: 1.0 }
    )

    observer.observe(dropdownRef.current)
    return () => observer.disconnect()
  }, [isOpenSelect, isInlineDropdown])

  // portal: 上方向に開いた場合、初回は推定高さ(DROPDOWN_MAX_HEIGHT)で配置されるため、
  // 候補が少なくトリガーとの間に隙間ができる。実際の高さを測ってトリガー直上に詰める。
  useLayoutEffect(() => {
    if (!isOpenSelect || isInlineDropdown) return
    if (dropdownStyle.openedBelow !== false) return
    const anchor = containerRef.current ?? selectRef.current
    const menu = dropdownRef.current
    if (!anchor || !menu) return
    const rect = anchor.getBoundingClientRect()
    const height = menu.getBoundingClientRect().height || menu.offsetHeight
    if (!height) return
    const desiredTop = rect.top - height - DROPDOWN_GAP
    setDropdownStyle((s) =>
      s.openedBelow === false && Math.abs((s.top ?? 0) - desiredTop) > 1 ? { ...s, top: desiredTop } : s
    )
  }, [isOpenSelect, isInlineDropdown, dropdownStyle.openedBelow])

  // portal: スクロール・リサイズ時はドロップダウンを閉じる。fixed 配置はスクロールや
  // ビューポートのサイズ変更（DevTools の表示・ドック変更、ウィンドウリサイズ等）に追従しないため、
  // 開いたままだとトリガーから浮いてズレる。閉じることでそのズレを防ぐ。
  useEffect(() => {
    if (!isOpenSelect || isInlineDropdown) return

    const close = () => {
      setOpenAbove(false)
      portalOpenedBelowRef.current = true
      setIsOpenSelect(false)
    }
    const closeOnScroll = (e) => {
      // ドロップダウン内部のスクロール（候補一覧）では閉じない
      if (dropdownRef.current?.contains(e.target)) return
      close()
    }

    window.addEventListener('scroll', closeOnScroll, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', closeOnScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [isOpenSelect, isInlineDropdown])

  const checkIfFocusUnderSelect = async () => selectRef.current && selectRef.current === (await getActiveElement())

  useToggleOnSpaceEnter({ functions: { checkIfFocusUnderSelect, toggleSelect } })
  useSelectOptionOnEnter({
    data: { optionsRef, dropdownRef },
    functions: { toggleSelect, handleSelect }
  })
  useArrowKeyToSelectOptions({
    data: {
      selectRef,
      options: filteredOptions,
      value
    },
    functions: { handleSelect }
  })

  // 展開直後に検索欄へフォーカス。state→DOM 同期目的の useEffect（許容例外）
  useEffect(() => {
    if (!searchable || !isOpenSelect) return
    const frameId = requestAnimationFrame(() => {
      searchInputRef.current?.focus()
      searchInputRef.current?.select()
    })
    return () => cancelAnimationFrame(frameId)
  }, [searchable, isOpenSelect])

  const dropdownMenu = isOpenSelect ? (
    <ul
      tabIndex={0}
      role="menu"
      ref={dropdownRef}
      className={`ui-select__list rounded-xl ${openAbove ? 'ui-select__list--above' : ''} ${dropdownClassName}`}
      style={
        isInlineDropdown
          ? isContentWidth
            ? { width: 'max-content', minWidth: '100%', maxWidth: `${dropdownMaxWidth}px` }
            : undefined
          : dropdownStyle.position
            ? {
                position: 'fixed',
                left: `${dropdownStyle.left}px`,
                top: `${dropdownStyle.top}px`,
                bottom: 'auto',
                zIndex: 9999,
                ...(isContentWidth
                  ? {
                      width: 'max-content',
                      minWidth: `${dropdownStyle.anchorWidth}px`,
                      maxWidth: `${dropdownMaxWidth}px`
                    }
                  : { width: `${dropdownStyle.anchorWidth}px` })
              }
            : { position: 'fixed', visibility: 'hidden' }
      }
    >
      {searchable && filteredOptions.length === 0 ? (
        <li role="menuitem" aria-disabled className="ui-select__item text-h7 text-gray-700/50">
          該当する項目がありません
        </li>
      ) : (
        filteredOptions.map((item, index) => (
          <li
            ref={(e) => {
              optionsRef.current[index] = e
            }}
            tabIndex={0}
            role="menuitem"
            data-option-value={item.value}
            onClick={(e) => handleSelect?.(e, item.value)}
            className="ui-select__item text-h7"
            key={index}
          >
            {item.label}
          </li>
        ))
      )}
    </ul>
  ) : null

  return (
    <div id={id} ref={containerRef} className={`ui-select min-w-52 ${className} `} role="select" aria-label={name}>
      <div
        role="select_control"
        ref={selectRef}
        tabIndex={disabled ? -1 : 0}
        onClick={() => toggleSelect()}
        className={
          `ui-select__btn flex items-center justify-between ` +
          ` rounded-xl pl-4 pr-2 ` +
          ` ${isOpenSelect && 'is-active'} ${disabled && 'is-disabled'} ` +
          ` ${isError && errorClasses} ${selectClassName}`
        }
      >
        {searchable && isOpenSelect ? (
          <input
            ref={searchInputRef}
            type="text"
            role="searchbox"
            className="text-h7 mr-2 min-w-0 flex-1 border-0 bg-transparent p-0 outline-none focus:outline-none"
            // グローバル input の枠線(box-shadow/border)を打ち消す。Tailwind より確実に上書きするため style で明示
            style={{ boxShadow: 'none', border: 'none' }}
            value={searchKeyword}
            placeholder={selectedLabel ?? ''}
            aria-label={name}
            data-testid={props?.['data-testid']}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
        ) : (
          <div
            className={`ui-select__text text-h7 mr-2 truncate ${lightNoSelect && textLight}`}
            title={selectedLabel ?? ''}
            aria-haspopup="menu"
            aria-expanded={isOpenSelect}
            aria-controls="menu"
            data-testid={props?.['data-testid']}
          >
            {selectedLabel}
          </div>
        )}
        <div className="ui-select__icon flex items-center justify-center w-8 h-8">
          <span className="icon-arrow"></span>
        </div>
      </div>
      {isInlineDropdown ? dropdownMenu : isOpenSelect && createPortal(dropdownMenu, document.body)}
    </div>
  )
}

/**
 * @function useSelectOptionOnEnter
 * @param {object} payload
 * @param {object} payload.data
 * @param {import('react').MutableRefObject<HTMLElement[]>} payload.data.optionsRef
 * @param {import('react').MutableRefObject<HTMLElement | null>} payload.data.dropdownRef
 * @param {object} payload.functions
 * @param {function( any ):void} payload.functions.handleSelect
 * @param {function():void} payload.functions.toggleSelect //切り替え関数
 */
export const useSelectOptionOnEnter = (payload) => {
  const {
    data: { optionsRef, dropdownRef },
    functions: { handleSelect, toggleSelect }
  } = payload

  useEffect(() => {
    const abortController = new AbortController()
    document.addEventListener(
      'keydown',
      async (e) => {
        const activeEl = await getActiveElement()
        const focusedOption = optionsRef.current?.find((e) => e === activeEl)

        if (e.key === 'Tab' && !dropdownRef.current?.contains(activeEl)) {
          toggleSelect(false)
        }

        if (!focusedOption) return

        if (['Enter'].includes(e.key)) {
          const data = focusedOption.dataset.optionValue
          handleSelect(undefined, data)
        }
      },
      { signal: abortController.signal }
    )
    return () => abortController.abort()
  }, [])
}

/**
 * @function useToggleOnSpaceEnter
 * @param {object} payload
 * @param {object} payload.functions
 * @param {function():Promise<boolean>} payload.functions.checkIfFocusUnderSelect
 * @param {function():void} payload.functions.toggleSelect //切り替え関数
 */
export const useToggleOnSpaceEnter = (payload) => {
  const {
    functions: { checkIfFocusUnderSelect, toggleSelect }
  } = payload

  useEffect(() => {
    const abortC = new AbortController()

    document.addEventListener(
      'keydown',
      async (e) => {
        if (['Enter', ' ', 'Spacebar', 'Space'].includes(e.key) && e.target.getAttribute('role') === 'select_control') {
          e.preventDefault()
          if (await checkIfFocusUnderSelect()) {
            toggleSelect()
          }
        }
      },
      { signal: abortC.signal }
    )

    return () => abortC.abort()
  }, [])
}

/**
 * @function useArrowKeyToSelectOptions
 * @param {object} payload
 * @param {object} payload.data
 * @param {import('react').MutableRefObject<HTMLElement[]>} payload.data.selectRef
 * @param {{ label: string; value: string }[]} payload.data.options
 * @param {string} payload.data.value
 * @param {object} payload.functions
 * @param {function( any ):void} payload.functions.handleSelect
 */
const useArrowKeyToSelectOptions = (payload) => {
  const {
    data: { selectRef, options, value },
    functions: { handleSelect }
  } = payload
  useEffect(() => {
    const abortC = new AbortController()

    document.addEventListener(
      'keydown',
      async (e) => {
        const activeEl = document.activeElement

        if (!selectRef.current) return

        if (!selectRef.current.contains(activeEl)) return

        if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
          e.preventDefault()
          e.stopPropagation()

          const currentIndex = options.findIndex((opt) => opt.value === value)
          let nextIndex = currentIndex

          if (e.key === 'ArrowDown') {
            nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % options.length
          } else if (e.key === 'ArrowUp') {
            nextIndex = currentIndex === -1 ? options.length - 1 : (currentIndex - 1 + options.length) % options.length
          }

          handleSelect(undefined, options[nextIndex].value)
        }
      },
      {
        signal: abortC.signal,
        passive: false
      }
    )

    return () => abortC.abort()
  }, [value])
}

/**
 * @typedef {Object} ChrofyCustomYearSelectProps
 * @property {string} id
 * @property {string} value
 * @property {function(Event):void} onChange
 * @property {string} className
 * @property {SelectOption[]} options
 * @property {boolean} disabled
 * @property {boolean} isError
 * @property {boolean} hasNoSelect
 * @property {string} selectClassName
 * @property {boolean} lightNoSelect
 *
 * @typedef {Object} SelectOption
 * @property {string} label
 * @property {string} value
 * @property {any} extra
 */

/**
 * @function ChrofyCustomYearSelect
 * @type {React.FC<ChrofyCustomYearSelectProps>} props
 */
export const ChrofyCustomYearSelect = (props) => {
  const {
    id,
    value,
    onChange,
    className,
    options: _options = [],
    disabled,
    isError = false,
    hasNoSelect = true,
    selectClassName = '',
    lightNoSelect = true,
    placeholder = '',
    selectionKey = 'value'
  } = props

  const [isOpenSelect, setIsOpenSelect] = useState(false)
  const selectRef = useRef(null)

  const options = hasNoSelect ? [{ label: '未選択', value: '' }, ..._options] : _options

  const textLight = lightNoSelect && value === '' ? ' text-gray-700/50 ' : ''
  const selectStyles = selectClassName + ' ' + textLight
  const errorClasses = ['is-error'].join(' ')

  const selectedLabel = options.find((e) => e[selectionKey] === value)?.label ?? placeholder

  const handleBlur = (e) => {
    if (selectRef.current && !selectRef.current.contains(e.relatedTarget)) {
      setIsOpenSelect(false)
    }
  }

  return (
    <div className="action-area flex">
      <div className={`action-area__year-select ${className}`}>
        <div className="year-container" ref={selectRef} onBlur={handleBlur}>
          <div
            id={id}
            className={`yearButton flex items-center justify-between rounded-xl pl-4 pr-2 ${selectStyles} ${disabled ? 'is-disabled' : ''} ${isOpenSelect ? 'is-active' : ''} ${isError ? errorClasses : ''}`}
            onClick={() => {
              if (!disabled) setIsOpenSelect((prev) => !prev)
            }}
            role="select_control"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpenSelect}
            data-testid={props?.['data-testid']}
          >
            <div className="yearButton__text text-h7 mr-2" aria-label="label">
              {selectedLabel}
            </div>
            <div className="yearButton__icon flex items-center justify-center w-8 h-8">
              <span className="icon-arrow"></span>
            </div>
          </div>

          <ul className="year rounded-xl" hidden={!isOpenSelect} role="listbox" tabIndex={-1}>
            {options.map((option, index) => {
              const label = typeof option === 'object' ? option.label : option
              const value = typeof option === 'object' ? option.value : option
              return (
                <li
                  key={index}
                  className="item text-h7"
                  onClick={() => {
                    onChange(value)
                    setIsOpenSelect(false)
                  }}
                  role="option"
                  tabIndex={0}
                  aria-selected={selectedLabel === label}
                >
                  {label}
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}
